/**
 * Price chart helpers: labels, size/rate formatting, the chart form and tier
 * grid (state, validation, request building), and property size handling.
 *
 * Validation mirrors the backend PriceChartBusinessLayer / PropertyBusinessLayer
 * so most mistakes are caught before a round-trip; the backend stays
 * authoritative. Pricing math is deliberately NOT here: prices are only ever
 * computed by the backend (POST /pricing/quote and job line saves).
 */

import * as types from '../api/types';

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

/** Same list, order and labels as backend SIZE_UNIT_DEFINITIONS (GET /price-charts/units). */
export const SIZE_UNIT_OPTIONS: types.SizeUnitOption[] = [
  { unit: 'SQFT', dimension: 'AREA', label: 'sq ft' },
  { unit: 'SQYD', dimension: 'AREA', label: 'sq yd' },
  { unit: 'SQM', dimension: 'AREA', label: 'sq m' },
  { unit: 'ACRE', dimension: 'AREA', label: 'acre' },
  { unit: 'HECTARE', dimension: 'AREA', label: 'hectare' },
  { unit: 'LINEAR_FT', dimension: 'LENGTH', label: 'linear ft' },
  { unit: 'METER', dimension: 'LENGTH', label: 'm' },
];

export const SIZE_DIMENSION_LABELS: Record<types.SizeDimension, string> = {
  AREA: 'Area',
  LENGTH: 'Length',
};

/** Labels that take a plural "s" ("2 acres"); abbreviations ("sq ft", "m") do not. */
const PLURAL_UNIT_LABELS: ReadonlySet<string> = new Set(['acre', 'hectare']);

export function isSizeUnit(value: unknown): value is types.SizeUnit {
  return typeof value === 'string' && (types.SIZE_UNITS as readonly string[]).includes(value);
}

/** "SQFT" -> "sq ft"; unknown units are returned unchanged. */
export function sizeUnitLabel(unit: string | null | undefined, units: types.SizeUnitOption[] = SIZE_UNIT_OPTIONS): string {
  if (!unit) return '';
  return units.find((u) => u.unit === unit)?.label ?? SIZE_UNIT_OPTIONS.find((u) => u.unit === unit)?.label ?? unit;
}

const sizeFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });

/** ("5000.00", "SQFT") -> "5,000 sq ft"; ("2", "ACRE") -> "2 acres". Empty string when either is missing. */
export function formatSize(value: string | number | null | undefined, unit: string | null | undefined): string {
  if (value === null || value === undefined || value === '' || !unit) return '';
  const n = Number(value);
  const label = sizeUnitLabel(unit);
  if (!Number.isFinite(n)) return `${value} ${label}`;
  const plural = PLURAL_UNIT_LABELS.has(label) && n !== 1 ? 's' : '';
  return `${sizeFormatter.format(n)} ${label}${plural}`;
}

/** A bare size number for tier bounds: "5000.00" -> "5,000". */
export function formatSizeNumber(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? sizeFormatter.format(n) : String(value);
}

// ---------------------------------------------------------------------------
// Money / rates
// ---------------------------------------------------------------------------

const rateFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/**
 * Rates and chart prices have up to 4 decimal places (Decimal(14,4)), so a
 * $0.005 overage must not round to $0.01: "0.005" -> "$0.005", "50" -> "$50.00".
 */
export function formatRate(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? rateFormatter.format(n) : String(value);
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const PRICING_TYPE_OPTIONS: types.PriceChartPricingType[] = ['BAND_FLAT', 'RATE_PER_UNIT'];

export const PRICING_TYPE_LABELS: Record<types.PriceChartPricingType, string> = {
  BAND_FLAT: 'Flat price per band',
  RATE_PER_UNIT: 'Rate per unit',
};

/** Help text for the pricing type, shown next to the tier grid. */
export const PRICING_TYPE_HELP: Record<types.PriceChartPricingType, string> = {
  BAND_FLAT:
    'Each band has one flat price. A size gets the price of the band it falls in; sizes up to the first bound get the first price.',
  RATE_PER_UNIT:
    "The first band's price is a flat minimum charge. Every other band's price is a rate per unit applied to the whole size " +
    '(size × rate, never less than the minimum), so totals step up or down at band edges.',
};

export const PRICE_SOURCE_LABELS: Record<types.JobPriceSource, string> = {
  CATALOG: 'Catalog',
  CHART: 'Chart',
  MANUAL: 'Manual',
};

/** A job line's price source; lines from before price charts have none and are catalog-priced. */
export function priceSourceOf(line: Pick<types.JobLineItem, 'priceSource'>): types.JobPriceSource {
  return line.priceSource ?? 'CATALOG';
}

/** "Chart price at 5,000 sq ft" for CHART lines with a size snapshot; null otherwise. */
export function chartLineDescription(
  line: Pick<types.JobLineItem, 'priceSource' | 'sizeSnapshot' | 'sizeUnitSnapshot'>
): string | null {
  if (priceSourceOf(line) !== 'CHART') return null;
  const size = formatSize(line.sizeSnapshot, line.sizeUnitSnapshot);
  return size ? `Chart price at ${size}` : 'Chart price';
}

// ---------------------------------------------------------------------------
// Decimal validation
// ---------------------------------------------------------------------------

/** Backend limits: sizes are Decimal(14,2), prices and rates Decimal(14,4). */
const SIZE_MAX_EXCLUSIVE = 1e12;
const PRICE_MAX_EXCLUSIVE = 1e10;

function decimalPattern(maxPlaces: number): RegExp {
  return new RegExp(`^\\d+(\\.\\d{1,${maxPlaces}})?$`);
}
const SIZE_PATTERN = decimalPattern(2);
const PRICE_PATTERN = decimalPattern(4);

function isNonNegativeDecimal(text: string, pattern: RegExp, maxExclusive: number): boolean {
  return pattern.test(text) && Number(text) < maxExclusive;
}

/** Numeric equality for decimal strings ("50" vs "50.0000"); blanks are equal only to blanks. */
function sameDecimal(a: string, b: string | null | undefined): boolean {
  if (b === null || b === undefined || b === '') return a === '';
  if (a === '') return false;
  return Number(a) === Number(b);
}

// ---------------------------------------------------------------------------
// Tier grid
// ---------------------------------------------------------------------------

/** Backend MAX_TIERS. */
export const MAX_TIERS = 100;

export interface TierRow {
  key: string;
  upToSize: string;
  price: string;
}

let tierKeySeq = 0;
export function newTierRow(upToSize = '', price = ''): TierRow {
  tierKeySeq += 1;
  return { key: `tier-${Date.now()}-${tierKeySeq}`, upToSize, price };
}

export function tierRowsFromTiers(tiers: types.PriceChartTier[] | undefined): TierRow[] {
  return [...(tiers ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((t) => ({ key: t.id, upToSize: t.upToSize, price: t.price }));
}

/**
 * The exclusive lower bound each row implies: the previous row's upToSize as
 * typed, "0" for the first row. Bands are defined only by their upper bound,
 * so there can be no gaps or overlaps.
 */
export function impliedLowerBounds(rows: Pick<TierRow, 'upToSize'>[]): string[] {
  return rows.map((_, index) => (index === 0 ? '0' : rows[index - 1].upToSize.trim() || '?'));
}

export interface TierValidation {
  /** One entry per row: the row's first problem, or null. */
  rowErrors: (string | null)[];
  /** A problem with the set as a whole (e.g. no bands), or null. */
  setError: string | null;
  valid: boolean;
}

/**
 * Same rules as backend validateTiers: at least one band; every upToSize > 0
 * with at most 2 decimals and strictly greater than the previous row's; every
 * price >= 0 with at most 4 decimals.
 */
export function validateTierRows(rows: Pick<TierRow, 'upToSize' | 'price'>[]): TierValidation {
  let setError: string | null = null;
  if (rows.length === 0) setError = 'Add at least one band';
  else if (rows.length > MAX_TIERS) setError = `A price chart can have at most ${MAX_TIERS} bands`;

  let previous: number | null = null;
  const rowErrors = rows.map((row) => {
    const upTo = row.upToSize.trim();
    const price = row.price.trim();
    let error: string | null = null;
    let upToNumber: number | null = null;

    if (!upTo) error = 'Enter the upper bound';
    else if (!isNonNegativeDecimal(upTo, SIZE_PATTERN, SIZE_MAX_EXCLUSIVE)) {
      error = 'Upper bound must be a number with at most 2 decimal places';
    } else {
      upToNumber = Number(upTo);
      if (upToNumber <= 0) error = 'Upper bound must be greater than 0';
      else if (previous !== null && upToNumber <= previous) {
        error = `Upper bound must be greater than the previous band's (${formatSizeNumber(previous)})`;
      }
    }

    if (!error) {
      if (!price) error = 'Enter a price';
      else if (!isNonNegativeDecimal(price, PRICE_PATTERN, PRICE_MAX_EXCLUSIVE)) {
        error = 'Price must be zero or more with at most 4 decimal places';
      }
    }

    // Keep comparing against the last parseable bound so one bad row does not
    // flag every row after it.
    if (upToNumber !== null && upToNumber > 0) previous = Math.max(previous ?? 0, upToNumber);
    return error;
  });

  return { rowErrors, setError, valid: setError === null && rowErrors.every((e) => e === null) };
}

export function tierRowsToInput(rows: Pick<TierRow, 'upToSize' | 'price'>[]): types.PriceChartTierInput[] {
  return rows.map((row) => ({ upToSize: row.upToSize.trim(), price: row.price.trim() }));
}

/** Whether the grid differs from the stored tiers (numerically; row order matters). */
export function tiersChanged(rows: Pick<TierRow, 'upToSize' | 'price'>[], tiers: types.PriceChartTier[] | undefined): boolean {
  const stored = tierRowsFromTiers(tiers);
  if (stored.length !== rows.length) return true;
  return rows.some(
    (row, i) => !sameDecimal(row.upToSize.trim(), stored[i].upToSize) || !sameDecimal(row.price.trim(), stored[i].price)
  );
}

/** The chart maximum (last band's upToSize) when it parses, else null. */
export function chartMaximum(rows: Pick<TierRow, 'upToSize'>[]): string | null {
  const last = rows[rows.length - 1]?.upToSize.trim();
  return last && Number.isFinite(Number(last)) && Number(last) > 0 ? last : null;
}

/** "Above 20,000 sq ft: top band + overage × amount above 20,000." */
export function overageExplanation(
  rows: Pick<TierRow, 'upToSize'>[],
  unit: string,
  pricingType: types.PriceChartPricingType
): string {
  const max = chartMaximum(rows);
  const maxText = max ? formatSize(max, unit) : 'the last band';
  const amountText = max ? formatSizeNumber(max) : 'the maximum';
  const top = pricingType === 'RATE_PER_UNIT' ? "the price at the maximum" : 'the top band price';
  return `Above ${maxText}: ${top} + overage rate × the amount above ${amountText}.`;
}

// ---------------------------------------------------------------------------
// Chart form
// ---------------------------------------------------------------------------

export const PRICE_CHART_NAME_MAX_LENGTH = 100;
export const PRICE_CHART_DESCRIPTION_MAX_LENGTH = 1000;

export interface PriceChartFormState {
  name: string;
  description: string;
  unit: types.SizeUnit;
  pricingType: types.PriceChartPricingType;
  overageRatePerUnit: string;
  active: boolean;
}

export const EMPTY_PRICE_CHART_FORM: PriceChartFormState = {
  name: '',
  description: '',
  unit: 'SQFT',
  pricingType: 'BAND_FLAT',
  overageRatePerUnit: '',
  active: true,
};

export function priceChartFormFromChart(chart: types.PriceChart): PriceChartFormState {
  return {
    name: chart.name,
    description: chart.description ?? '',
    unit: chart.unit,
    pricingType: chart.pricingType,
    overageRatePerUnit: chart.overageRatePerUnit,
    active: chart.active,
  };
}

/** Returns an error message, or null when the chart fields are valid. */
export function validatePriceChartForm(form: PriceChartFormState): string | null {
  const name = form.name.trim();
  if (!name) return 'Name is required';
  if (name.length > PRICE_CHART_NAME_MAX_LENGTH) return `Name must be ${PRICE_CHART_NAME_MAX_LENGTH} characters or less`;
  if (form.description.trim().length > PRICE_CHART_DESCRIPTION_MAX_LENGTH) {
    return `Description must be ${PRICE_CHART_DESCRIPTION_MAX_LENGTH} characters or less`;
  }
  if (!isSizeUnit(form.unit)) return 'Select a unit';
  if (!PRICING_TYPE_OPTIONS.includes(form.pricingType)) return 'Select a pricing type';
  const overage = form.overageRatePerUnit.trim();
  if (!overage) return 'Overage rate is required (enter 0 for no overage charge)';
  if (!isNonNegativeDecimal(overage, PRICE_PATTERN, PRICE_MAX_EXCLUSIVE)) {
    return 'Overage rate must be zero or more with at most 4 decimal places';
  }
  return null;
}

export function buildCreatePriceChartRequest(
  form: PriceChartFormState,
  rows: Pick<TierRow, 'upToSize' | 'price'>[]
): types.CreatePriceChartRequest {
  const description = form.description.trim();
  return {
    name: form.name.trim(),
    ...(description ? { description } : {}),
    unit: form.unit,
    pricingType: form.pricingType,
    overageRatePerUnit: form.overageRatePerUnit.trim(),
    active: form.active,
    tiers: tierRowsToInput(rows),
  };
}

/** PATCH body with only the changed fields; a cleared description is sent as null. */
export function buildUpdatePriceChartRequest(
  form: PriceChartFormState,
  chart: types.PriceChart
): types.UpdatePriceChartRequest {
  const request: types.UpdatePriceChartRequest = {};
  const name = form.name.trim();
  if (name !== chart.name) request.name = name;
  const description = form.description.trim();
  if (description !== (chart.description ?? '')) request.description = description || null;
  if (form.unit !== chart.unit) request.unit = form.unit;
  if (form.pricingType !== chart.pricingType) request.pricingType = form.pricingType;
  const overage = form.overageRatePerUnit.trim();
  if (!sameDecimal(overage, chart.overageRatePerUnit)) request.overageRatePerUnit = overage;
  if (form.active !== chart.active) request.active = form.active;
  return request;
}

/**
 * Charts offered in an item's chart picker: active charts plus the item's
 * current chart even if it is inactive (so opening the form never silently
 * unlinks it), sorted by name.
 */
export function selectableCharts(charts: types.PriceChart[], currentChartId: string | null | undefined): types.PriceChart[] {
  return charts
    .filter((c) => c.active || c.id === currentChartId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// Property size
// ---------------------------------------------------------------------------

/** Both-or-neither, like backend PropertyBusinessLayer.validateSize. Returns an error or null. */
export function validatePropertySize(value: string, unit: string): string | null {
  const v = value.trim();
  if (!v && !unit) return null;
  if (v && !unit) return 'Select a unit for the property size, or clear the size';
  if (!v && unit) return 'Enter the property size, or clear the unit';
  if (!isSizeUnit(unit)) return 'Select a valid size unit';
  if (!isNonNegativeDecimal(v, SIZE_PATTERN, SIZE_MAX_EXCLUSIVE)) {
    return 'Property size must be zero or more with at most 2 decimal places';
  }
  return null;
}

/** The size as request fields: both null when cleared. Call after validatePropertySize. */
export function propertySizeFields(value: string, unit: string): { sizeValue: string | null; sizeUnit: types.SizeUnit | null } {
  const v = value.trim();
  return v && isSizeUnit(unit) ? { sizeValue: v, sizeUnit: unit } : { sizeValue: null, sizeUnit: null };
}

/** Whether the form's size differs from the property's stored size. */
export function propertySizeChanged(
  value: string,
  unit: string,
  property: Pick<types.Property, 'sizeValue' | 'sizeUnit'>
): boolean {
  const next = propertySizeFields(value, unit);
  const currentUnit = property.sizeUnit ?? null;
  return !sameDecimal(next.sizeValue ?? '', property.sizeValue ?? null) || next.sizeUnit !== currentUnit;
}
